import { useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, RefreshCcw } from "lucide-react";
import { debounce } from "lodash";

import TableLoad from "components/load/TableLoad";
import PageError from "components/page-error";
import { PageTitle, PageLayout, ContentContainer } from "components/layout";
import { Button } from "components/ui/button";
import { Input } from "components/ui/input";
import { Label } from "components/ui/label";

import { Meta } from "@/@types/paginated";
import { handeOrderBy } from "@/utils/pagination";
import { columns } from "./columns";
import SelectFilters from "./components/select-filters";
import { useRegionalSubstitutionData } from "./hooks/useData";
import Table from "../components/table";
import { EmptyState } from "../components/empty-state";
import { REGIONAL_SUBSTITUTION_ROUTES } from "../constants/routes";
import { useRegionalSubstitutionFilterActions } from "../ducks/filter/hooks/actions";
import { useFilterState } from "../ducks/filter/hooks/filterState";

export const RegionalSubstitutionList: React.FC = () => {
  const { updateFilter } = useRegionalSubstitutionFilterActions();
  const filterState = useFilterState();
  const { filters } = filterState || { filters: {} };
  const navigate = useNavigate();

  const orderBy = handeOrderBy(filters.order || {});
  const orderByString = JSON.stringify(orderBy);

  const fallbackMeta: Meta = {
    currentPage: filters?.meta?.page || 1,
    lastPage: 1,
    perPage: filters?.meta?.perPage || 10,
    total: 0,
    prev: null,
    next: null,
  };

  const { substitutions, meta, isLoading, error, refetch } =
    useRegionalSubstitutionData({
      search: filters.search || "",
      status: filters.status || "",
      orderBy: orderByString,
      meta: {
        page: filters?.meta?.page || 1,
        perPage: filters?.meta?.perPage || 10,
      },
    });

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    updateFilter({ key: "search", value: event.target.value });
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedHandleInputChange = useCallback(
    debounce(handleInputChange, 400),
    [],
  );

  const handleCreate = () => {
    navigate(REGIONAL_SUBSTITUTION_ROUTES.CREATE);
  };

  const renderContent = () => {
    if (isLoading) return <TableLoad />;
    if (substitutions.length === 0) {
      return (
        <EmptyState
          title="Nenhuma substituição encontrada"
          description="Não há vínculos de substituição cadastrados no momento."
          actionLabel="Novo Vínculo"
          onAction={handleCreate}
        />
      );
    }
    return (
      <Table
        data={substitutions}
        columns={columns}
        meta={meta ?? fallbackMeta}
        filters={filters}
        updateFilter={updateFilter}
      />
    );
  };

  if (error) {
    return (
      <PageError
        status={500}
        message="Ocorreu um erro interno. Por favor, tente novamente mais tarde."
      />
    );
  }

  return (
    <PageLayout>
      <div className="flex w-full items-center justify-between">
        <PageTitle
          title="Substituição de Regional"
          description="Gerencie substituições temporais de Regional por Gerente"
        />
        <div className="flex gap-2">
          <Button
            variant="outline"
            className="h-8"
            onClick={() => refetch()}
            disabled={isLoading}
          >
            <RefreshCcw className="mr-2 h-4 w-4" />
            Atualizar
          </Button>
          <Button className="h-8" onClick={handleCreate}>
            <Plus className="mr-2 h-4 w-4" />
            Novo Vínculo
          </Button>
        </div>
      </div>
      <ContentContainer>
        <div className="flex flex-col gap-4">
          <div className="flex items-end gap-4">
            <div className="flex flex-col gap-2">
              <Label htmlFor="buscar-substituicoes" className="text-sm">
                Buscar
              </Label>
              <Input
                id="buscar-substituicoes"
                placeholder="Buscar por regional ou gerente..."
                onChange={debouncedHandleInputChange}
                className="h-8 max-w-sm"
              />
            </div>
            <SelectFilters />
          </div>

          {renderContent()}
        </div>
      </ContentContainer>
    </PageLayout>
  );
};
